import { buttonVariants } from '../components/ui/Button';

const BENEFITS = [
  'Pipelines incrementales con gobierno de datos desde el día uno.',
  'Capas Raw → Curada → Datamarts con linaje completo y observabilidad.',
  'Modelo FinOps integrado para mantener el costo predecible al escalar.',
  'Habilitación para IA generativa y agentes sobre tu propio corpus.',
];

const NODES = [
  { num: '01', label: 'Fuentes' },
  { num: '02', label: 'Ingesta' },
  { num: '03', label: 'Curación', isKey: true },
  { num: '04', label: 'Consumo' },
];

const LAYERS = [
  { tag: 'RAW',       desc: 'Landing zone con linaje y trazabilidad' },
  { tag: 'CURADA',    desc: 'Modelo de negocio gobernado y validado' },
  { tag: 'DATAMARTS', desc: 'Consumo por dominio + IA / BI / DaaS' },
];

export default function Arkeflow() {
  return (
    <section id="arkeflow" className="on-blue bg-etribe-blue text-white py-[120px] relative overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(40% 60% at 90% 30%, rgba(255,200,54,.08), transparent 70%)' }}
      />
      <div className="container-etribe relative">
        <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-14 items-center">
          <div>
            <p className="eyebrow"><span>Framework propio</span></p>
            <h2 className="text-[clamp(40px,5vw,64px)] mb-5 text-white">
              Conoce <span className="text-etribe-yellow">#</span>ARKEFLOW
            </h2>
            <p className="text-[clamp(17px,1.3vw,20px)] text-white/80 mb-8 leading-[1.55] text-justify hyphens-auto">
              Nuestro framework propio de Data Management. Una arquitectura modular que estructura la ingesta, curación y consumo de datos en cualquier industria — diseñada por nuestros arquitectos para acortar el camino del dato crudo a la decisión.
            </p>

            <ul className="mb-10">
              {BENEFITS.map((b, i) => (
                <li
                  key={b}
                  className={[
                    'flex items-start gap-3.5 py-3.5 border-t border-white/15 text-[15px] text-white/90',
                    i === BENEFITS.length - 1 ? 'border-b' : '',
                  ].join(' ')}
                >
                  <span className="w-5.5 h-5.5 rounded-md bg-etribe-yellow text-etribe-blue grid place-items-center flex-shrink-0 mt-px">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                      <path d="M5 12l5 5L20 7" />
                    </svg>
                  </span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <a href="#contacto" className={buttonVariants({ variant: 'accent', size: 'lg' })}>
              Solicita una demo
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </a>
          </div>

          {/* Diagram */}
          <div className="relative bg-white/[0.04] border border-white/15 rounded-[20px] p-10" aria-hidden>
            {/* Animated SVG flow with data packets */}
            <svg
              className="absolute inset-10 w-[calc(100%-5rem)] h-[calc(100%-5rem)] pointer-events-none z-0"
              viewBox="0 0 400 300"
              preserveAspectRatio="none"
            >
              <g
                fill="none"
                stroke="rgba(255,255,255,.14)"
                strokeWidth="1.5"
                strokeDasharray="4 6"
                style={{ animation: 'arkdash 3s linear infinite' }}
              >
                <path d="M50 30 L 150 30" />
                <path d="M150 30 L 250 30" />
                <path d="M250 30 L 350 30" />
                <path d="M50 30 L 50 120 L 70 130" />
                <path d="M200 30 L 200 130" />
                <path d="M350 30 L 350 120 L 330 130" />
              </g>
              {/* Travelling packets */}
              <circle r="3" fill="#FFC836" style={{ filter: 'drop-shadow(0 0 6px rgba(255,200,54,.7))' }}>
                <animateMotion dur="3s" repeatCount="indefinite" path="M50 30 L 150 30 L 250 30 L 350 30" />
              </circle>
              <circle r="2.5" fill="#FFC836" style={{ filter: 'drop-shadow(0 0 6px rgba(255,200,54,.7))' }}>
                <animateMotion dur="4s" begin="1s" repeatCount="indefinite" path="M50 30 L 150 30 L 250 30 L 350 30" />
              </circle>
              <circle r="2" fill="#FFC836" style={{ filter: 'drop-shadow(0 0 6px rgba(255,200,54,.7))' }}>
                <animateMotion dur="5s" begin="2s" repeatCount="indefinite" path="M50 30 L 150 30 L 250 30 L 350 30" />
              </circle>
            </svg>
            <style>{`
              @keyframes arkdash { to { stroke-dashoffset: -40; } }
            `}</style>

            <div className="relative z-10">
              <p className="text-[11px] tracking-[0.14em] uppercase text-white/60 mb-3">Flujo del dato</p>
              <div className="grid grid-cols-4 gap-2 mb-4">
                {NODES.map((n) => (
                  <div
                    key={n.num}
                    className={[
                      'text-center py-4 px-2 rounded-[10px] text-[12px] font-semibold tracking-[0.04em] border transition-all hover:-translate-y-0.5',
                      n.isKey
                        ? 'bg-etribe-yellow text-etribe-blue border-etribe-yellow'
                        : 'bg-white/[0.06] border-white/15 hover:bg-white/15',
                    ].join(' ')}
                  >
                    <span className="font-heading text-[22px] block mb-1">{n.num}</span>
                    {n.label}
                  </div>
                ))}
              </div>

              <div className="mt-6 pt-6 border-t border-white/15">
                <p className="text-[11px] tracking-[0.14em] uppercase text-white/60 mb-3">Capas de arquitectura</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {LAYERS.map((l) => (
                    <div key={l.tag} className="p-4 rounded-md bg-white/[0.05] text-[12px] font-medium">
                      <span className="font-heading text-[11px] text-etribe-yellow tracking-[0.12em] block mb-1.5">{l.tag}</span>
                      {l.desc}
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-white/15">
                <p className="text-[11px] tracking-[0.14em] uppercase text-white/60 mb-3">Pilares transversales</p>
                <div className="grid grid-cols-3 gap-2">
                  {['Gobierno', 'Observabilidad', 'FinOps'].map((p) => (
                    <div key={p} className="p-4 rounded-md bg-white/[0.05] text-[12px] font-medium text-center sm:text-left">{p}</div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
